import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Briefcase, Users } from 'lucide-react';
import OkiruLogo from './OkiruLogo';
import CourseCover from './CourseCover';
import ImageQuote, { Illustration } from './ui/ImageQuote';
import Footer from './Footer';
import { IMAGES } from '../data/learningImagery';
import { QUOTES } from '../data/educationQuotes';
import './PublicLanding.css';

export function groupCoursesByCategory(courses) {
  const groups = new Map();
  courses.forEach(course => {
    const category = typeof course.category === 'string' && course.category.trim()
      ? course.category.trim()
      : 'Other courses';
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(course);
  });
  return Array.from(groups, ([category, items]) => ({ category, courses: items }));
}

export default function PublicLanding({ onSignIn }) {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const catalogueRef = useRef(null);

  useEffect(() => {
    fetch('/api/public/courses')
      .then(response => response.json())
      .then(json => { if (json.success && Array.isArray(json.data)) setCourses(json.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visibleCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter(course => `${course.title} ${course.description} ${course.category}`.toLowerCase().includes(term));
  }, [courses, search]);
  const courseGroups = useMemo(() => groupCoursesByCategory(visibleCourses), [visibleCourses]);

  const browse = () => {
    catalogueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    catalogueRef.current?.focus({ preventScroll: true });
  };

  return (
    <div className="public-page">
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="Okiru Learn home">
          <OkiruLogo height={30} />
          <span>Learn</span>
        </a>
        <button type="button" className="btn btn-primary public-sign-in" onClick={onSignIn}>Sign in</button>
      </header>

      <main>
        <section className="public-hero" aria-labelledby="public-hero-title">
          <div className="public-hero-copy">
            <p className="public-eyebrow">OKIRU LEARN</p>
            <h1 id="public-hero-title">Professional learning for work and beyond</h1>
            <p className="public-hero-support">Practical courses in AI, B-BBEE, Employment Equity, Skills Development, ESG and business skills.</p>
            <div className="public-hero-actions">
              <button type="button" className="btn btn-primary" onClick={onSignIn}>Sign in</button>
              <button type="button" className="btn btn-ghost" onClick={browse}>Browse courses</button>
            </div>
          </div>
          <ImageQuote image={IMAGES.groupDiscussion} quote={QUOTES.aristotleOrnament} layout="overlay" eager />
        </section>

        <section className="public-features" aria-label="About learning with Okiru">
          <article className="public-feature public-feature--quote">
            <div className="public-feature-copy">
              <p className="public-eyebrow">LEARNING THAT CONNECTS</p>
              <h2>Make space for learning in the work you do</h2>
              <p>Explore practical courses in AI, B-BBEE, Employment Equity, Skills Development, ESG and business skills, with clear descriptions to help you choose where to begin.</p>
            </div>
            <ImageQuote image={IMAGES.facilitatorFlipchart} quote={QUOTES.plutarchKindling} layout="overlay" />
          </article>

          <article className="public-feature public-feature--reverse">
            <div className="public-feature-copy">
              <p className="public-eyebrow">A CLEAR NEXT STEP</p>
              <h2>Keep your learning organised</h2>
              <p>When you sign in, your courses and progress are gathered in one place, including any required learning and its due date.</p>
            </div>
            <div className="public-learning-visual" aria-label="Illustration of course subjects available on Okiru Learn">
              <Illustration image={IMAGES.learnerLaptop} />
              <div className="public-subject-panel">
                <span>Explore by subject</span>
                <ul>
                  <li><BookOpen size={17} aria-hidden="true" /> Technology</li>
                  <li><Briefcase size={17} aria-hidden="true" /> Business</li>
                  <li><Users size={17} aria-hidden="true" /> Professional skills</li>
                  <li><Briefcase size={17} aria-hidden="true" /> Transformation and compliance</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        <section className="public-catalogue" aria-labelledby="public-catalogue-title" ref={catalogueRef} tabIndex={-1}>
          <div className="public-catalogue-heading">
            <div>
              <p className="public-eyebrow">COURSE CATALOGUE</p>
              <h2 id="public-catalogue-title">Browse courses</h2>
            </div>
            <label className="public-search">
              <span>Search courses</span>
              <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Title, description or subject" />
            </label>
          </div>

          {loading ? <p className="public-catalogue-status" role="status">Loading courses…</p> : null}
          {!loading && visibleCourses.length === 0 ? <p className="public-catalogue-status" role="status">No courses match that search.</p> : null}
          {courseGroups.map(({ category, courses: categoryCourses }) => (
            <section className="public-course-group" aria-label={`${category} courses`} key={category}>
              <h3 className="public-course-group-heading">{category}</h3>
              <div className="public-course-grid">
                {categoryCourses.map(course => (
                  <article className="public-course-card" key={course.id}>
                    <CourseCover courseId={course.id} title={course.title} category={course.category} decorative compact />
                    <div className="public-course-copy">
                      <h3>{course.title}</h3>
                      <p>{course.description}</p>
                      <p className="public-course-level"><span>Level</span> {course.level}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </section>

        <section className="public-about" aria-labelledby="public-about-title">
          <div className="public-about-copy">
            <p className="public-eyebrow">ABOUT US</p>
            <h2 id="public-about-title">About Okiru</h2>
            <p>Okiru is a South African transformation, compliance and AI business. Okiru Learn brings our courses together in one place, from B-BBEE, Employment Equity, Skills Development and ESG to practical AI and business skills. Learners can explore the catalogue, complete assigned learning and track their progress.</p>
            <button type="button" className="btn btn-ghost" onClick={browse}>Explore the course catalogue</button>
          </div>
          <Illustration image={IMAGES.colleaguesOnlineLearning} />
        </section>

        <section className="public-faq" aria-labelledby="public-faq-title">
          <p className="public-eyebrow">GOOD TO KNOW</p>
          <h2 id="public-faq-title">Frequently asked questions</h2>
          <div className="public-faq-list">
            <details>
              <summary>Can I browse courses before signing in?</summary>
              <p>Yes. The public catalogue shows course titles, short descriptions and levels. Course lessons are available after sign-in.</p>
            </details>
            <details>
              <summary>How do I sign in?</summary>
              <p>Use the sign-in button and continue with your emailed PIN, Okiru.app account or Microsoft account.</p>
            </details>
            <details>
              <summary>Where can I see my course progress?</summary>
              <p>After signing in, open Your courses to see your enrolled courses, progress and any required due dates.</p>
            </details>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
